import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Row, Col, Modal, Badge, Container } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../services/api';
import { DEPARTMENTS } from '../constants/departments';
import './Profile.css';

const Profile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState({});
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [showContactModal, setShowContactModal] = useState(false);
  const [managerName, setManagerName] = useState('No Manager');
  const [isEditing, setIsEditing] = useState(false);
  const [newContact, setNewContact] = useState({
    name: '',
    relation_type: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        phone: user.phone || '',
        designation: user.designation || '',
        department: user.department || '',
      });
      fetchEmergencyContacts();
      if (user.manager_id) {
        fetchManagerName(user.manager_id);
      }
    }
  }, [user]);

  const fetchEmergencyContacts = async () => {
    try {
      const response = await usersAPI.getEmergencyContacts();
      setEmergencyContacts(response.data);
    } catch (error) {
      console.error('Error fetching emergency contacts:', error);
    }
  };

  const fetchManagerName = async (managerId) => {
    try {
      // Try to fetch all users first (works for HR/Admin)
      const allUsersResponse = await usersAPI.getAllUsers();
      const manager = allUsersResponse.data.find(u => u.id === managerId);
      if (manager) {
        setManagerName(manager.full_name);
      } else {
        setManagerName('Unknown');
      }
    } catch (error) {
      // If getAllUsers fails (employee role), try direct getUser
      try {
        const response = await usersAPI.getUser(managerId);
        setManagerName(response.data.full_name);
      } catch (err) {
        console.error('Error fetching manager:', err);
        setManagerName('Manager ID: ' + managerId);
      }
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.updateMe(profileData);
      alert('Profile updated successfully!');
      setIsEditing(false);
      window.location.reload();
    } catch (error) {
      alert('Error updating profile');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();
    try {
      await usersAPI.addEmergencyContact(newContact);
      setShowContactModal(false);
      setNewContact({ name: '', relation_type: '', phone: '', email: '' });
      fetchEmergencyContacts();
      alert('Emergency contact added!');
    } catch (error) {
      alert('Error adding contact');
    }
  };

  const handleDeleteContact = async (id) => {
    if (window.confirm('Delete this contact?')) {
      try {
        await usersAPI.deleteEmergencyContact(id);
        fetchEmergencyContacts();
      } catch (error) {
        alert('Error deleting contact');
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Container fluid className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar-section">
          <div className="profile-avatar">
            {getInitials(user?.full_name)}
          </div>
          <div className="profile-header-info">
            <h2 className="profile-name">{user?.full_name || 'User'}</h2>
            <p className="profile-email">{user?.email}</p>
            <Badge bg="primary" className="role-badge">{user?.role || 'Employee'}</Badge>
          </div>
        </div>
      </div>

      <Row className="mt-4">
        <Col lg={8}>
          <Card className="profile-card mb-4">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="section-title mb-0">Personal Information</h5>
                {!isEditing && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="edit-button"
                  >
                    <i className="bi bi-pencil"></i> Edit Profile
                  </Button>
                )}
              </div>

              <Form onSubmit={handleProfileUpdate}>
                <Row className="mb-3">
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Full Name</label>
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          value={profileData.full_name || ''}
                          onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
                          className="modern-input"
                        />
                      ) : (
                        <div className="info-value">{profileData.full_name || '-'}</div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Email</label>
                      <div className="info-value">{user?.email || '-'}</div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Phone</label>
                      {isEditing ? (
                        <Form.Control
                          type="tel"
                          value={profileData.phone || ''}
                          onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                          className="modern-input"
                        />
                      ) : (
                        <div className="info-value">{profileData.phone || '-'}</div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Designation</label>
                      {isEditing ? (
                        <Form.Control
                          type="text"
                          value={profileData.designation || ''}
                          onChange={(e) => setProfileData({ ...profileData, designation: e.target.value })}
                          className="modern-input"
                        />
                      ) : (
                        <div className="info-value">{profileData.designation || '-'}</div>
                      )}
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Department</label>
                      {isEditing ? (
                        <Form.Select
                          value={profileData.department || ''}
                          onChange={(e) => setProfileData({ ...profileData, department: e.target.value })}
                          className="modern-input"
                        >
                          <option value="">Select Department</option>
                          {DEPARTMENTS.map((dept) => (
                            <option key={dept} value={dept}>
                              {dept}
                            </option>
                          ))}
                        </Form.Select>
                      ) : (
                        <div className="info-value">{profileData.department || '-'}</div>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="info-field">
                      <label className="info-label">Role</label>
                      <div className="info-value">{user?.role || '-'}</div>
                    </div>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={12}>
                    <div className="info-field">
                      <label className="info-label">Reporting Manager</label>
                      <div className="info-value">{managerName}</div>
                    </div>
                  </Col>
                </Row>

                {isEditing && (
                  <div className="action-buttons">
                    <Button
                      type="submit"
                      variant="primary"
                      className="save-button"
                    >
                      Save Changes
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={() => setIsEditing(false)}
                      className="cancel-button"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="profile-card emergency-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="section-title mb-0">Emergency Contacts</h5>
                <Button
                  size="sm"
                  variant="success"
                  onClick={() => setShowContactModal(true)}
                  className="add-contact-button"
                >
                  + Add
                </Button>
              </div>

              {emergencyContacts.length === 0 ? (
                <div className="empty-state">
                  <i className="bi bi-person-plus text-muted" style={{ fontSize: '3rem' }}></i>
                  <p className="text-muted mt-2">No contacts added</p>
                </div>
              ) : (
                <div className="contacts-list">
                  {emergencyContacts.map((contact) => (
                    <div key={contact.id} className="contact-item">
                      <div className="contact-info">
                        <div className="contact-avatar">
                          {getInitials(contact.name)}
                        </div>
                        <div>
                          <div className="contact-name">{contact.name}</div>
                          <div className="contact-relationship">{contact.relation_type}</div>
                          <div className="contact-phone">{contact.phone}</div>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="link"
                        className="delete-contact-button"
                        onClick={() => handleDeleteContact(contact.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showContactModal} onHide={() => setShowContactModal(false)} centered className="contact-modal">
        <Modal.Header closeButton>
          <Modal.Title>Add Emergency Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleAddContact}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                required
                value={newContact.name}
                onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                placeholder="Enter contact name"
                className="modern-input"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Relationship</Form.Label>
              <Form.Control
                required
                value={newContact.relation_type}
                onChange={(e) => setNewContact({ ...newContact, relation_type: e.target.value })}
                placeholder="e.g., Spouse, Parent, Sibling"
                className="modern-input"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                required
                type="tel"
                value={newContact.phone}
                onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                placeholder="Enter phone number"
                className="modern-input"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email (Optional)</Form.Label>
              <Form.Control
                type="email"
                value={newContact.email}
                onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                placeholder="Enter email address"
                className="modern-input"
              />
            </Form.Group>
            <div className="d-flex gap-2 justify-content-end">
              <Button variant="outline-secondary" onClick={() => setShowContactModal(false)}>
                Cancel
              </Button>
              <Button type="submit" variant="success">
                Add Contact
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default Profile;
